import { useState, useEffect, useCallback, useRef } from 'react';
import { getContacts, deleteContact, updateContact } from '../../../services/contactService';
import { useAdminNotification } from '../../../contexts/AdminNotificationContext';

const POLL_INTERVAL = 5000;
const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 5;

const ContactsManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [contacts, setContacts] = useState<any[]>([]);
  const [stats, setStats] = useState({ total: 0, new: 0, inProgress: 0, completed: 0, highPriority: 0 });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editStatus, setEditStatus] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editAdminNotes, setEditAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { refreshNotifications } = useAdminNotification();

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
      searchTimeoutRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [filterStatus]);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await getContacts({
        page,
        limit: PAGE_SIZE,
        status: filterStatus === 'all' ? undefined : filterStatus,
        search: searchTerm.trim() || undefined,
      });
      setContacts(res?.contacts ?? []);
      setTotal(res?.total ?? 0);
      setTotalPages(res?.totalPages ?? 1);
      setStats(res?.stats ?? { total: 0, new: 0, inProgress: 0, completed: 0, highPriority: 0 });
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, searchTerm]);

  useEffect(() => {
    setLoading(true);
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    const timer = setInterval(fetchContacts, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchContacts]);

  const formatContact = (c: any) => ({
    id: c._id,
    name: c.name,
    email: c.email,
    phone: c.phone || '—',
    service: c.serviceName || '—',
    message: c.message,
    status: c.status || 'new',
    priority: c.priority || 'medium',
    adminNotes: c.adminNotes || '',
    createdAt: c.createdAt ? new Date(c.createdAt).toLocaleString('vi-VN') : '—',
    source: c.source || 'Website',
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return 'Mới';
      case 'in-progress':
        return 'Đang xử lý';
      case 'completed':
        return 'Hoàn thành';
      case 'cancelled':
        return 'Đã hủy';
      default:
        return 'Không xác định';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'Cao';
      case 'medium':
        return 'Trung bình';
      case 'low':
        return 'Thấp';
      default:
        return 'Không xác định';
    }
  };

  const canDeleteContact = (status: string) =>
    status === 'completed' || status === 'cancelled';

  useEffect(() => {
    if (selectedContact) {
      setEditStatus(selectedContact.status || 'new');
      setEditPriority(selectedContact.priority || 'medium');
      setEditAdminNotes(selectedContact.adminNotes || '');
    }
  }, [selectedContact]);

  const handleUpdateContact = async () => {
    if (!selectedContact) return;
    setUpdating(true);
    try {
      await updateContact(selectedContact.id, {
        status: editStatus,
        priority: editPriority,
        adminNotes: editAdminNotes,
      });
      setSelectedContact({ ...selectedContact, status: editStatus, priority: editPriority, adminNotes: editAdminNotes });
      fetchContacts();
      refreshNotifications();
    } catch (err: any) {
      alert(err.response?.data?.errorMessage || err.message || 'Không thể cập nhật');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteContact = async (contact: { id: string; name: string; status: string }) => {
    if (!canDeleteContact(contact.status)) return;
    if (!window.confirm(`Bạn có chắc muốn xóa liên hệ từ "${contact.name}"?`)) return;
    try {
      await deleteContact(contact.id);
      if (page > 1 && filteredContacts.length === 1) {
        setPage(page - 1);
      } else {
        fetchContacts();
      }
    } catch (err: any) {
      alert(err.response?.data?.errorMessage || err.message || 'Không thể xóa liên hệ');
    }
  };

  const filteredContacts = contacts.map(formatContact);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Quản lý liên hệ</h2>
          <p className="text-gray-600">Theo dõi và xử lý các yêu cầu liên hệ từ khách hàng</p>
        </div>
        <button className="bg-[#00C897] hover:bg-[#00C897]/90 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors">
          <i className="ri-download-line"></i>
          Xuất Excel
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <i className="ri-message-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{loading ? '...' : stats.total}</h3>
              <p className="text-gray-600 text-sm">Tổng liên hệ</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{loading ? '...' : stats.inProgress}</h3>
              <p className="text-gray-600 text-sm">Đang xử lý</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{loading ? '...' : stats.completed}</h3>
              <p className="text-gray-600 text-sm">Hoàn thành</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
              <i className="ri-alert-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{loading ? '...' : stats.highPriority}</h3>
              <p className="text-gray-600 text-sm">Ưu tiên cao</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Tìm kiếm theo tên, email, dịch vụ..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="md:w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent pr-8"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="new">Mới</option>
              <option value="in-progress">Đang xử lý</option>
              <option value="completed">Hoàn thành</option>
              <option value="cancelled">Đã hủy</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Khách hàng</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Dịch vụ</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Trạng thái</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Ưu tiên</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Nguồn</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Ngày tạo</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && filteredContacts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-500">
                    <i className="ri-loader-4-line animate-spin text-2xl inline-block mb-2"></i>
                    <p>Đang tải...</p>
                  </td>
                </tr>
              ) : filteredContacts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-500">
                    Chưa có liên hệ nào
                  </td>
                </tr>
              ) : filteredContacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-gray-50">
                  <td className="py-4 px-6">
                    <div>
                      <h3 className="font-medium text-gray-900">{contact.name}</h3>
                      <p className="text-sm text-gray-600">{contact.email}</p>
                      <p className="text-sm text-gray-600">{contact.phone}</p>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                      {contact.service}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(contact.status)}`}>
                      {getStatusText(contact.status)}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${getPriorityColor(contact.priority)}`}>
                      {getPriorityText(contact.priority)}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-gray-900">{contact.source}</td>
                  <td className="py-4 px-6 text-gray-900">{contact.createdAt}</td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setSelectedContact(contact)}
                        className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                        <i className="ri-mail-line"></i>
                      </button>
                      {canDeleteContact(contact.status) ? (
                        <button
                          onClick={() => handleDeleteContact(contact)}
                          title="Xóa liên hệ"
                          className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      ) : (
                        <span
                          title="Chỉ xóa được khi trạng thái Hoàn thành hoặc Đã hủy"
                          className="w-8 h-8 flex items-center justify-center text-gray-300 cursor-not-allowed rounded-lg"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Hiển thị {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} trong {total} liên hệ
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg ${
                      page === p
                        ? 'bg-[#2A4B7C] text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Contact Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Chi tiết liên hệ</h3>
                <button 
                  onClick={() => setSelectedContact(null)}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Customer Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Thông tin khách hàng</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Họ tên</label>
                      <p className="text-gray-900">{selectedContact.name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Email</label>
                      <p className="text-gray-900">{selectedContact.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Số điện thoại</label>
                      <p className="text-gray-900">{selectedContact.phone}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Dịch vụ quan tâm</label>
                      <p className="text-gray-900">{selectedContact.service}</p>
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Nội dung liên hệ</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 leading-relaxed">{selectedContact.message}</p>
                  </div>
                </div>

                {/* Status & Priority */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Trạng thái</label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent pr-8"
                    >
                      <option value="new">Mới</option>
                      <option value="in-progress">Đang xử lý</option>
                      <option value="completed">Hoàn thành</option>
                      <option value="cancelled">Đã hủy</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Ưu tiên</label>
                    <select
                      value={editPriority}
                      onChange={(e) => setEditPriority(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent pr-8"
                    >
                      <option value="low">Thấp</option>
                      <option value="medium">Trung bình</option>
                      <option value="high">Cao</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Nguồn</label>
                    <p className="text-gray-900 mt-1 py-2">{selectedContact.source}</p>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Ghi chú xử lý</label>
                  <textarea
                    value={editAdminNotes}
                    onChange={(e) => setEditAdminNotes(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent resize-none"
                    placeholder="Thêm ghi chú về quá trình xử lý..."
                  />
                </div>
                
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={handleUpdateContact}
                    disabled={updating}
                    className="flex-1 bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-semibold transition-colors"
                  >
                    {updating ? 'Đang cập nhật...' : 'Cập nhật'}
                  </button>
                  <button className="px-6 bg-[#00C897] hover:bg-[#00C897]/90 text-white py-3 rounded-lg font-semibold transition-colors">
                    Gửi email
                  </button>
                  <button
                    onClick={() => setSelectedContact(null)}
                    className="px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors"
                  >
                    Đóng
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsManagement;
